import { Toast } from 'patsnap-biz';
import { useI18n } from 'vue-i18n';
import { openMessageBox } from '@eureka/shared/confirm-box';
import type { NavMenuItem } from '../types';
import { NAV_MENU_TYPE } from '../types';
import { removeHistory, removeAllRecords } from '../infrastructure/api-functions';
import { CommonApis } from '@eureka/services/src/apis/common.api';
import { cn, tw, en, jp } from '../infrastructure/locales';
import { useNavBarState } from './use-navbar-store';
import { EVENTS_CONFIG, MENU_LIST_CONFIG } from '../constants';
import { computed, onMounted, ref, watch } from 'vue';
import type { IControl } from '@eureka/utils/control';
import { controlDeferred } from '@eureka/utils/control';
import { useRoute, useRouter } from 'vue-router';
import { type HeaderService } from '@patsnap-utils/biz';
import _ from 'lodash';
import { isUserLogin } from '@eureka/utils/account';

type HeaderServiceWithExtendAttrs = HeaderService & {
    ignoreDetailIdDetect: boolean // 忽略详情id检测, 用于某些特殊场景下, 比如monitor
}

export const useNavBar = (props: { navModuleType: NAV_MENU_TYPE, headerService: HeaderServiceWithExtendAttrs }, searchTaskDialogRef?: any) => {
    const { t } = useI18n({
        messages: {
            cn, en, jp, tw,
        },
    });

    const historyPreTitleMessages = ref({
        cn: {
            HistoryType: cn.HistoryType,
        },
        en: {
            HistoryType: en.HistoryType,
        },
        jp: {
            HistoryType: jp.HistoryType,
        },
        tw: {
            HistoryType: tw.HistoryType,
        },
    })

    const control = ref<IControl>();

    const isUserLoginValue = ref(false);

    const route = useRoute();

    const router = useRouter();

    const { historyList, updateHistory, queryList, currentDetailId, getSessionsTaskData, opened } = useNavBarState();

    const navHistory = computed(() => {
        return {
            /**
             * 为了兼容组件库，暂时先添加 id 属性
             */
            id: 'history',
            type: NAV_MENU_TYPE.HISTORY,
            icon: 'SolidTimeRefresh',
            title: t(`NavBar.${NAV_MENU_TYPE.HISTORY}`),
            children: historyList.value,
            style: 'margin-top: 16px;',
        };
    });

    function confirmSaveBoardChange(data: { title?: string; content?: string } = {}) {
        return openMessageBox({
            title: data.title || t('NavBar.deleteTitle'),
            message: data.content || t('NavBar.deleteContent'),
            confirmButtonText: t('NavBar.confirm'),
            cancelButtonText: t('NavBar.cancel'),
        });
    }

    async function removeHistoryItem(historyId: string) {
        const confirm = await confirmSaveBoardChange()
        if (!confirm) return
        removeHistory(historyId).then(() => {
            updateHistory(_.filter(historyList.value, nav => nav.id !== historyId));
            return Toast({
                message: t('NavBar.deleteSuccess'),
                type: 'success',
            });
        })
    }
    /**
     * 同一个模块内使用vue-router跳转
     * 不同模块使用window.open跳转
     */
    function navigateTo(menu: NavMenuItem) {
        // 如果点击的是 Search Task 菜单，打开弹窗
        if (menu.type === NAV_MENU_TYPE.SEARCH_TASK && searchTaskDialogRef) {
            searchTaskDialogRef.value?.openDialog();
            getSessionsTaskData();
            return;
        }

        // 如果点击的是 Customize List 菜单，折叠左侧导航并跳转
        if (menu.type === NAV_MENU_TYPE.CUSTOMIZE_LIST) {
            opened.value = false;
        }

        const isSamePackage = menu.module === props.navModuleType || menu.module === route.meta.navModuleType
        if (isSamePackage && menu.router) {
            router.push(menu.router)
        } else {
            const isOtherSite = /^(http|https):\/\//.test(menu.link || '')
            window.open(menu.link, isOtherSite ? '_blank' : '_self')
        }
    }

    function track(type: string, detail: string) {
        CommonApis.track({
            event_type: type,
            event_detail_str: detail,
            module_type: 'EUREKA',
            source_type: 'air',
            other_param: route.name as string,
        })
    }

    function viewResult(menu: NavMenuItem) {
        navigateTo(menu);
        const event = _.get(EVENTS_CONFIG, menu.type, EVENTS_CONFIG.HOME)
        track(event, menu.type);
    }

    function onRemoveHistory(data: NavMenuItem) {
        if (!data.id) return;
        removeHistoryItem(data.id);
    }

    async function removeAllHistory() {
        const confirm = await confirmSaveBoardChange({
            title: t('NavBar.deleteAllTitle'),
            content: t('NavBar.deleteAllContent'),
        })
        if (!confirm) return
        removeAllRecords().then(() => {
            updateHistory([]);
            Toast({
                message: t('NavBar.deleteSuccess'),
                type: 'success',
            });
        })
    }

    const handleMenuActive = (item: NavMenuItem) => {
        if (item.children?.length) {
            return false
        }
        if (currentDetailId.value && !props.headerService?.ignoreDetailIdDetect) {
            return false
        }
        if (route.meta.navModuleType) {
            return item.type === route.meta.navModuleType
        }
        return item.type === props.navModuleType
    }

    const navMenuList = computed(() => {
        const menus = MENU_LIST_CONFIG.filter((item) => {
            if (item.authKey === 'isUserLogin') {
                return isUserLoginValue.value;
            }
            if (item.authKey) {
                return control.value?.[item.authKey as keyof IControl]
            }
            return true
        })
        return _.map(menus, item => {
            return {
                ...item,
                /**
                 * 为了兼容组件库，暂时先添加 id 属性
                 */
                id: item.type,
                title: t(`NavBar.${item.type}`),
                active: handleMenuActive(item),
                onClick: viewResult,
                children: _.map((item as any).children, child => {
                    /**
                     * 同一个页面可能根据不同的入口，高亮不同的菜单，无法配置在meta。
                     */
                    const subPageNavModuleType = route.meta.navModuleType as NAV_MENU_TYPE || route.query.nav_module as NAV_MENU_TYPE
                    return {
                        ...child,
                        title: t(`NavBar.${child.type}`),
                        active: child.type === subPageNavModuleType,
                        onClick: viewResult,
                    }
                }),
            };
        });
    });

    onMounted(async() => {
        control.value = await controlDeferred.promise;
        isUserLoginValue.value = await isUserLogin();
        queryList();
    })

    // 监听路由变化，刷新历史记录
    watch(() => route.path, () => {
        queryList();
    })

    return {
        historyPreTitleMessages,
        viewResult,
        removeHistoryItem,
        removeAllHistory,
        onRemoveHistory,
        navHistory,
        navMenuList,
    }
}
